import { useEffect, useRef, useState } from "react";
import { Crop, ImageUp, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { uploadImage } from "@/components/ImageUploader";
import { PhotoFocusEditor, focusStyle } from "@/components/profile/PhotoFocusEditor";

export const ANGELS = [
  { id: "emerald", name: "Emerald", src: "/brand/emerald-v2.webp" },
  { id: "ruby", name: "Ruby", src: "/brand/ruby-v2.webp" },
  { id: "sapphire", name: "Sapphire", src: "/brand/sapphire-v3.webp" },
];
export const HERO_SRC = "/brand/cosmos.webp";
const siteImage = (path, fallback) => (path ? `${process.env.REACT_APP_BACKEND_URL}/api/site/image/${path}` : fallback);

export const useAngelFocus = () => {
  const [data, setData] = useState({});
  useEffect(() => { api.get("/site/angels").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  return [data, setData];
};
export const angelSrc = (data, angel) => siteImage(data?.srcs?.[angel.id], angel.src);

export const useHero = () => {
  const [hero, setHero] = useState({ focus: null, src: null });
  useEffect(() => { api.get("/site/hero").then(({ data }) => setHero(data)).catch(() => {}); }, []);
  return [hero, setHero];
};
export const heroSrc = (hero) => siteImage(hero?.src, HERO_SRC);

export const AngelCircle = ({ angel, src, focus, size = 80, testId }) => (
  <span className="block shrink-0 overflow-hidden rounded-full border border-border" style={{ width: size, height: size }}>
    <img src={src || angel.src} alt={`${angel.name}, a Genesis Angel`} loading="lazy" className="img-cover object-top" style={focusStyle(focus)} data-testid={testId} />
  </span>
);

const ImageActions = ({ testId, custom, onUpload, onRestore, onAdjust }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP image");
    if (file.size > 8 * 1024 * 1024) return toast.error("Images must be 8MB or smaller");
    setBusy(true);
    try { await onUpload(await uploadImage(file)); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  return (
    <div className="flex flex-wrap justify-center gap-2">
      <button type="button" className="btn-outline h-9 px-3 text-xs" onClick={onAdjust} data-testid={`${testId}-adjust`}><Crop className="h-3.5 w-3.5" /> Adjust</button>
      <button type="button" className="btn-outline h-9 px-3 text-xs" disabled={busy} onClick={() => input.current?.click()} data-testid={`${testId}-replace`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageUp className="h-3.5 w-3.5" />} Replace photo
      </button>
      {custom && <button type="button" className="btn-link h-9 text-xs" onClick={onRestore} data-testid={`${testId}-restore`}><RotateCcw className="h-3.5 w-3.5" /> Restore original</button>}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testId}-input`} />
    </div>
  );
};

export const AngelFraming = () => {
  const [data, setData] = useAngelFocus();
  const [editing, setEditing] = useState(null);
  const call = async (fn, msg) => { try { const { data: d } = await fn(); setData(d); toast.success(msg); } catch (e) { toast.error(apiError(e)); } };
  const save = (id, f) => call(() => api.put(`/admin/site/angels/${id}`, { x: f.x, y: f.y, zoom: f.zoom }), "Homepage portrait updated");
  const setImage = (a, path) => call(() => api.put(`/admin/site/angels/${a.id}/image`, { path }), path ? `${a.name}'s photo replaced. Adjust the framing if needed.` : `${a.name}'s original photo restored`);
  return (
    <section className="surface mt-8 p-6" data-testid="admin-angel-framing">
      <h2 className="font-display text-2xl">Homepage portraits</h2>
      <p className="mt-1 text-sm text-muted-foreground">Replace or frame the Genesis Angels as they appear in "The Club" section. Changes show on the homepage immediately.</p>
      <ul className="mt-5 flex flex-wrap gap-8">
        {ANGELS.map((a) => (
          <li key={a.id} className="flex max-w-[220px] flex-col items-center gap-2">
            <AngelCircle angel={a} src={angelSrc(data, a)} focus={data[a.id]} size={96} testId={`admin-angel-${a.id}`} />
            <span className="text-sm">{a.name}</span>
            <ImageActions testId={`admin-angel-${a.id}`} custom={!!data?.srcs?.[a.id]} onAdjust={() => setEditing(a)}
              onUpload={(p) => setImage(a, p)} onRestore={() => setImage(a, null)} />
          </li>
        ))}
      </ul>
      {editing && (
        <PhotoFocusEditor path={angelSrc(data, editing)} focus={data[editing.id] ? { ...data[editing.id], path: angelSrc(data, editing) } : null} open
          onOpenChange={(o) => !o && setEditing(null)} onSave={(f) => save(editing.id, f)} title={`Adjust ${editing.name}`} />
      )}
    </section>
  );
};

export const HeroFraming = () => {
  const [hero, setHero] = useHero();
  const [open, setOpen] = useState(false);
  const src = heroSrc(hero);
  const call = async (fn, msg) => { try { const { data } = await fn(); setHero(data); toast.success(msg); } catch (e) { toast.error(apiError(e)); } };
  const save = (f) => call(() => api.put("/admin/site/hero", f ? { x: f.x, y: f.y, zoom: f.zoom } : null), f ? "Hero image framing updated" : "Hero framing reset");
  const setImage = (path) => call(() => api.put("/admin/site/hero/image", { path }), path ? "Hero image replaced. Adjust the framing if needed." : "Original hero image restored");
  return (
    <section className="surface mt-6 p-6" data-testid="admin-hero-framing">
      <h2 className="font-display text-2xl">Homepage hero image</h2>
      <p className="mt-1 text-sm text-muted-foreground">Replace the hero image or choose which part stays in view. It's shown wide on phones and tall on desktop, so keep faces near the centre.</p>
      <div className="mt-5 flex flex-wrap items-end gap-5">
        <span className="block h-[150px] w-[250px] overflow-hidden rounded-xl border border-border">
          <img src={src} alt="Homepage hero" className="img-cover object-[center_22%]" style={focusStyle(hero.focus)} data-testid="admin-hero-preview" />
        </span>
        <span className="block h-[150px] w-[100px] overflow-hidden rounded-xl border border-border" aria-hidden>
          <img src={src} alt="" className="img-cover object-[center_22%]" style={focusStyle(hero.focus)} />
        </span>
        <div className="flex flex-col items-start gap-2">
          <ImageActions testId="admin-hero" custom={!!hero.src} onAdjust={() => setOpen(true)} onUpload={setImage} onRestore={() => setImage(null)} />
          {hero.focus && <button type="button" className="btn-link text-xs" onClick={() => save(null)} data-testid="admin-hero-reset">Reset framing</button>}
        </div>
      </div>
      {open && <PhotoFocusEditor path={src} focus={hero.focus ? { ...hero.focus, path: src } : { path: src, x: 50, y: 22, zoom: 1 }} open onOpenChange={setOpen} onSave={save}
        title="Adjust the hero image" shape="wide" hint="Drag to choose what stays in view, and zoom to crop tighter." />}
    </section>
  );
};

export const CARDS = [
  { id: "days_at_sea", title: "Days at sea", src: "/brand/days-at-sea.webp", pos: "center 45%" },
  { id: "villa_weekends", title: "Villa weekends", src: "/brand/villa-weekend.webp", pos: "center 55%" },
  { id: "alpine_chalets", title: "Alpine chalets", src: "/brand/alpine-chalet.webp", pos: "center 55%" },
];

export const useCards = () => {
  const [cards, setCards] = useState({});
  useEffect(() => { api.get("/site/cards").then(({ data }) => setCards(data)).catch(() => {}); }, []);
  return [cards, setCards];
};

export const cardImage = (cards, card) => {
  const c = cards?.[card.id] || {};
  return { src: siteImage(c.src, card.src), style: c.focus ? focusStyle(c.focus) : { objectPosition: card.pos } };
};

export const CardFraming = () => {
  const [cards, setCards] = useCards();
  const [editing, setEditing] = useState(null);
  const call = async (fn, msg) => { try { const { data } = await fn(); setCards(data); toast.success(msg); } catch (e) { toast.error(apiError(e)); } };
  const save = (id, f) => call(() => api.put(`/admin/site/cards/${id}`, { x: f.x, y: f.y, zoom: f.zoom }), "Card framing updated");
  const setImage = (c, path) => call(() => api.put(`/admin/site/cards/${c.id}/image`, { path }), path ? `${c.title} photo replaced. Adjust the framing if needed.` : `${c.title} original photo restored`);
  return (
    <section className="surface mt-6 p-6" data-testid="admin-card-framing">
      <h2 className="font-display text-2xl">Homepage inspiration cards</h2>
      <p className="mt-1 text-sm text-muted-foreground">Replace or frame the three "Where it could take you" photos. Changes show on the homepage immediately.</p>
      <ul className="mt-5 grid gap-6 sm:grid-cols-3">
        {CARDS.map((c) => {
          const img = cardImage(cards, c);
          return (
            <li key={c.id} className="flex flex-col items-center gap-3">
              <span className="block aspect-[4/3] w-full overflow-hidden rounded-xl border border-border">
                <img src={img.src} alt={c.title} className="img-cover" style={img.style} data-testid={`admin-card-${c.id}`} />
              </span>
              <span className="text-sm">{c.title}</span>
              <ImageActions testId={`admin-card-${c.id}`} custom={!!cards?.[c.id]?.src} onAdjust={() => setEditing(c)}
                onUpload={(p) => setImage(c, p)} onRestore={() => setImage(c, null)} />
            </li>
          );
        })}
      </ul>
      {editing && (
        <PhotoFocusEditor path={cardImage(cards, editing).src} focus={cards[editing.id]?.focus ? { ...cards[editing.id].focus, path: cardImage(cards, editing).src } : null} open
          onOpenChange={(o) => !o && setEditing(null)} onSave={(f) => save(editing.id, f)} title={`Adjust ${editing.title}`} shape="wide" hint="Drag to choose what stays in view, and zoom to crop tighter." />
      )}
    </section>
  );
};
